import { useState, useRef, useEffect } from "react";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreateTagDialog } from "@/components/tags/CreateTagDialog";
import { TagList } from "@/components/tags/TagList";
import { TagGrid } from "@/components/tags/TagGrid";
import ViewToggle from "@/components/ViewToggle";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { mockCustomerTags } from "@/utils/customerTags";

interface Tag {
  id: string;
  name: string;
  description: string;
  type: string;
  color: string;
  icon?: string;
  criteria: any;
  created_at: string;
  created_by: string;
  customers: string[];
}

export default function TagsPage() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("tagsViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "grid";
  });
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const tagListRef = useRef<{ refreshTags: () => void }>(null);
  const { toast } = useToast();

  const fetchTags = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("tags")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Convert mock tags to Tag format
      const mockTags = mockCustomerTags.map(tag => ({
        id: tag.id,
        name: tag.name,
        description: `${tag.type === 'static' ? 'Static' : 'Dynamic'} tag`,
        type: tag.type,
        color: tag.color || '#6b7280',
        icon: undefined,
        criteria: null,
        created_at: new Date().toISOString(),
        created_by: 'system',
        customers: [tag.customerId]
      }));
      
      // Combine database tags with mock tags, preferring database tags
      const dbTagIds = new Set((data || []).map(t => t.id));
      const allTags = [
        ...(data || []),
        ...mockTags.filter(t => !dbTagIds.has(t.id))
      ];
      
      setTags(allTags);
    } catch (error) {
      console.error("Error fetching tags:", error);
      toast({
        title: "Error",
        description: "Failed to fetch tags. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTag = async (id: string) => {
    try {
      const { error } = await supabase
        .from("tags")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setTags(tags.filter(tag => tag.id !== id));
      toast({
        title: "Tag deleted",
        description: "Tag has been deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting tag:", error);
      toast({
        title: "Error",
        description: "Failed to delete tag. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleTagCreated = () => {
    setIsCreateDialogOpen(false);
    fetchTags();
  };

  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    localStorage.setItem("tagsViewPreference", view);
  }, [view]);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 p-6 w-full">
      <div className="w-full mx-auto space-y-6">
        <div className="flex justify-between items-start mb-8">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Tags</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              Organize devices with static tags or create dynamic tags with custom criteria
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <ViewToggle view={view} onViewChange={setView} />
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Tag
              </Button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {view === "list" ? (
              <TagList ref={tagListRef} searchQuery={searchQuery} />
            ) : (
              <TagGrid 
                tags={tags.filter((tag) =>
                  tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  tag.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  tag.type.toLowerCase().includes(searchQuery.toLowerCase())
                )}
                onDelete={deleteTag}
              />
            )}
          </>
        )}

        <CreateTagDialog 
          open={isCreateDialogOpen} 
          onOpenChange={setIsCreateDialogOpen}
          onTagCreated={handleTagCreated}
        />
      </div>
    </div>
  );
}